class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.isHeaderSearch = this.classList.contains('header__search');
    this.searchModal = this.querySelector('.search-modal');
    this.searchModalPlaceholder = null;
    this.panelSlot = null;
    this.onBodyClickEvent = this.onBodyClick.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));

    if (this.searchModal) {
      this.searchModal.addEventListener('click', (event) => {
        if (event.target.closest('.search-modal__close-button--infield, .search-modal__close-button--external')) {
          event.preventDefault();
          this.close();
        }
      });
    }
  }

  connectedCallback() {
    if (!this.isHeaderSearch) return;

    this.mobileMedia = window.matchMedia('(max-width: 989px)');
    this.mobileMedia.addEventListener('change', this.onViewportChange);
  }

  disconnectedCallback() {
    this.mobileMedia?.removeEventListener('change', this.onViewportChange);
  }

  getPanelSlot() {
    return document.querySelector('[data-header-mobile-search-panel]');
  }

  mountMobilePanel() {
    this.panelSlot = this.getPanelSlot();
    if (!this.panelSlot || !this.searchModal || this.searchModal.dataset.inPanel === 'true') return;

    this.searchModalPlaceholder = document.createComment('header-search-modal');
    this.searchModal.parentNode.insertBefore(this.searchModalPlaceholder, this.searchModal);
    this.panelSlot.appendChild(this.searchModal);
    this.searchModal.dataset.inPanel = 'true';
    this.panelSlot.hidden = true;
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  isMobileHeaderSearch() {
    return this.isHeaderSearch && window.matchMedia('(max-width: 989px)').matches;
  }

  getSearchRoot() {
    return this.searchModal?.querySelector('predictive-search, search-form') || this.querySelector('predictive-search, search-form');
  }

  unmountMobilePanel() {
    if (!this.searchModal || this.searchModal.dataset.inPanel !== 'true' || !this.searchModalPlaceholder) return;

    this.searchModalPlaceholder.parentNode.insertBefore(this.searchModal, this.searchModalPlaceholder);
    this.searchModalPlaceholder.remove();
    this.searchModalPlaceholder = null;
    delete this.searchModal.dataset.inPanel;

    if (this.panelSlot) {
      this.panelSlot.hidden = true;
    }
  }

  onViewportChange(event) {
    if (event.matches) {
      this.mountMobilePanel();
      if (!this.isOpen()) {
        this.hideMobilePanel();
      }
      return;
    }

    this.unmountMobilePanel();
    document.body.classList.remove('header-search-sheet-open');
  }

  showMobilePanel() {
    this.panelSlot = this.getPanelSlot();
    if (!this.panelSlot) return;
    this.panelSlot.hidden = false;
    document.body.classList.add('header-search-sheet-open');
  }

  hideMobilePanel() {
    if (!this.panelSlot) return;
    this.panelSlot.hidden = true;
    document.body.classList.remove('header-search-sheet-open');
  }

  activateMobileSearch() {
    const searchRoot = this.getSearchRoot();
    const input = this.searchModal?.querySelector('input[type="search"]');

    if (searchRoot?.showPopularSuggestions) {
      searchRoot.showPopularSuggestions();
    }

    if (input) {
      input.focus({ preventScroll: true });
    }
  }

  deactivateMobileSearch() {
    const searchRoot = this.getSearchRoot();

    if (searchRoot?.hidePopularSuggestions) {
      searchRoot.hidePopularSuggestions();
    }

    this.searchModal?.querySelector('input[type="search"]')?.blur();
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    const insideTrigger = this.contains(event.target);
    const insidePanel = this.getPanelSlot()?.contains(event.target);

    if ((insideTrigger || insidePanel) && !event.target.classList.contains('modal-overlay')) {
      return;
    }

    this.close(false);
  }

  open(event) {
    this.detailsContainer.setAttribute('open', true);

    if (this.isMobileHeaderSearch()) {
      this.mountMobilePanel();
      this.showMobilePanel();
      requestAnimationFrame(() => this.activateMobileSearch());
    } else {
      document.body.classList.add('overflow-hidden');
    }

    setTimeout(() => {
      document.body.addEventListener('click', this.onBodyClickEvent);
    }, 0);

    const focusRoot = this.searchModal || this.detailsContainer;
    trapFocus(
      focusRoot.querySelector('[tabindex="-1"]'),
      focusRoot.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    const activeElement = document.activeElement;
    const focusInside =
      (activeElement && this.contains(activeElement)) ||
      (activeElement && this.panelSlot?.contains(activeElement));

    if (focusInside && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }

    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');

    if (this.isHeaderSearch) {
      this.deactivateMobileSearch();

      if (this.isMobileHeaderSearch()) {
        this.hideMobilePanel();
      }
    }
  }
}

customElements.define('details-modal', DetailsModal);
